import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Upload,
  message,
  Typography,
  Tag,
  Space,
  Divider,
  Alert
} from 'antd';
import { HomeOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import '../pages/MemberManagement.css';

const { Text, Paragraph, Title } = Typography;

type MerchantProfile = {
  _id: string;
  name?: string;
  merchantCode?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: string;
  remark?: string;
  principalName?: string;
  principalIdNumber?: string;
  businessLicenseUrl?: string;
  commitmentLetterUrl?: string;
  reviewStatus?: string;
  reviewNote?: string;
  pendingProfile?: Record<string, string> | null;
};

const REVIEW_LABEL: Record<string, { color: string; text: string }> = {
  pending: { color: 'orange', text: '入驻审核中' },
  approved: { color: 'green', text: '已通过' },
  rejected: { color: 'red', text: '已驳回' },
  pending_rereview: { color: 'gold', text: '资料变更待复审' }
};

async function merchantFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/merchant${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    }
  });
  return res.json();
}

async function uploadMerchantDoc(file: File): Promise<string> {
  const token = localStorage.getItem('token');
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/upload/merchant-document', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd
  });
  const body = await res.json();
  if (!res.ok || !body.success || !body.url) {
    throw new Error(body.error || '上传失败');
  }
  return body.url as string;
}

const resolveUrl = (url?: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
};

const MerchantShopCenter: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [profileForm] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [licenseFileList, setLicenseFileList] = useState<UploadFile[]>([]);
  const [commitmentFileList, setCommitmentFileList] = useState<UploadFile[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await merchantFetch('/me');
      if (!body.success) {
        message.error(body.error || '加载失败');
        return;
      }
      const m = body.data.merchant as MerchantProfile;
      setMerchant(m);
      const src = m.pendingProfile && m.reviewStatus === 'pending_rereview' ? { ...m, ...m.pendingProfile } : m;
      profileForm.setFieldsValue({
        name: src.name,
        contactName: src.contactName,
        contactPhone: src.contactPhone,
        contactEmail: src.contactEmail,
        address: src.address,
        remark: src.remark,
        principalName: src.principalName,
        principalIdNumber: src.principalIdNumber,
        businessLicenseUrl: src.businessLicenseUrl,
        commitmentLetterUrl: src.commitmentLetterUrl
      });
      setLicenseFileList(
        src.businessLicenseUrl
          ? [{ uid: '-1', name: '营业执照', status: 'done', url: resolveUrl(src.businessLicenseUrl) }]
          : []
      );
      setCommitmentFileList(
        src.commitmentLetterUrl
          ? [{ uid: '-2', name: '承诺书', status: 'done', url: resolveUrl(src.commitmentLetterUrl) }]
          : []
      );
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  }, [profileForm]);

  useEffect(() => {
    void load();
  }, [load]);

  const submitProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      setSaving(true);
      const body = await merchantFetch('/me/profile', {
        method: 'PUT',
        body: JSON.stringify(values)
      });
      if (!body.success) {
        message.error(body.error || '提交失败');
        return;
      }
      message.success(body.message || '已提交');
      void load();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('提交失败');
    } finally {
      setSaving(false);
    }
  };

  const submitPassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      setPwdSaving(true);
      const body = await merchantFetch('/me/password', {
        method: 'PUT',
        body: JSON.stringify(values)
      });
      if (!body.success) {
        message.error(body.error || '修改失败');
        return;
      }
      message.success('密码已更新');
      pwdForm.resetFields();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('修改失败');
    } finally {
      setPwdSaving(false);
    }
  };

  const review = merchant?.reviewStatus || 'pending';
  const meta = REVIEW_LABEL[review] || { color: 'default', text: review };

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>商家端</span>
        <span className="mm-crumb-sep">/</span>
        <span>商铺中心</span>
      </nav>

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">商铺中心</h1>
          <p className="mm-subtitle mb-0">
            管理商铺资料与登录密码。商铺信息、联系人及资质变更须经平台重新审核后生效；修改密码即时生效。
          </p>
        </div>
      </header>

      <div className="content-area px-1 px-md-2" style={{ maxWidth: 880 }}>
        {review !== 'approved' && (
          <Alert
            className="mb-3"
            type={review === 'rejected' ? 'error' : 'warning'}
            showIcon
            message={meta.text}
            description={
              merchant?.reviewNote
                ? `审核备注：${merchant.reviewNote}`
                : review === 'pending' || review === 'pending_rereview'
                  ? '审核通过前，实验室用户端可能无法看到本商铺。'
                  : undefined
            }
          />
        )}

        <Card loading={loading} className="mb-3">
          <Space wrap className="mb-3">
            <Text type="secondary">商家编号</Text>
            <Text strong>{merchant?.merchantCode || '—'}</Text>
            <Tag color={meta.color}>{meta.text}</Tag>
          </Space>

          <Title level={5}>商铺与联系信息</Title>
          <Paragraph type="secondary" className="mb-3">
            提交后进入平台审核；通过前仍展示当前已生效资料。
          </Paragraph>
          <Form form={profileForm} layout="vertical">
            <Form.Item name="name" label="商铺名称" rules={[{ required: true, message: '必填' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="contactName" label="联系人">
              <Input />
            </Form.Item>
            <Form.Item name="contactPhone" label="联系电话">
              <Input />
            </Form.Item>
            <Form.Item name="contactEmail" label="联系邮箱">
              <Input />
            </Form.Item>
            <Form.Item name="address" label="商铺地址">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item
              name="principalName"
              label="负责人姓名（与身份证一致）"
              rules={[{ required: true, message: '必填' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="principalIdNumber"
              label="负责人身份证号"
              rules={[
                { required: true, message: '必填' },
                { pattern: /^\d{17}[\dXx]$/, message: '请输入18位身份证号' }
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="businessLicenseUrl" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="commitmentLetterUrl" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="营业执照" required>
              <Upload
                fileList={licenseFileList}
                maxCount={1}
                beforeUpload={async (file) => {
                  try {
                    const url = await uploadMerchantDoc(file);
                    profileForm.setFieldsValue({ businessLicenseUrl: url });
                    setLicenseFileList([
                      { uid: file.uid, name: file.name, status: 'done', url: resolveUrl(url) }
                    ]);
                    message.success('营业执照已上传');
                  } catch (e) {
                    message.error(e instanceof Error ? e.message : '上传失败');
                  }
                  return false;
                }}
                onRemove={() => {
                  profileForm.setFieldsValue({ businessLicenseUrl: '' });
                  setLicenseFileList([]);
                }}
              >
                <Button icon={<UploadOutlined />}>上传营业执照</Button>
              </Upload>
            </Form.Item>
            <Form.Item
              label={
                <span>
                  承诺书扫描件{' '}
                  <a href="/merchant/commitment-template" target="_blank" rel="noreferrer">
                    下载/打印模板
                  </a>
                </span>
              }
              required
            >
              <Upload
                fileList={commitmentFileList}
                maxCount={1}
                beforeUpload={async (file) => {
                  try {
                    const url = await uploadMerchantDoc(file);
                    profileForm.setFieldsValue({ commitmentLetterUrl: url });
                    setCommitmentFileList([
                      { uid: file.uid, name: file.name, status: 'done', url: resolveUrl(url) }
                    ]);
                    message.success('承诺书已上传');
                  } catch (e) {
                    message.error(e instanceof Error ? e.message : '上传失败');
                  }
                  return false;
                }}
                onRemove={() => {
                  profileForm.setFieldsValue({ commitmentLetterUrl: '' });
                  setCommitmentFileList([]);
                }}
              >
                <Button icon={<UploadOutlined />}>上传承诺书</Button>
              </Upload>
            </Form.Item>
            <Button type="primary" loading={saving} onClick={() => void submitProfile()}>
              提交资料变更审核
            </Button>
          </Form>
        </Card>

        <Card>
          <Title level={5}>修改登录密码</Title>
          <Paragraph type="secondary">密码修改即时生效，无需平台审核。</Paragraph>
          <Form form={pwdForm} layout="vertical" style={{ maxWidth: 420 }}>
            <Form.Item
              name="currentPassword"
              label="当前密码"
              rules={[{ required: true, message: '请输入当前密码' }]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '至少 6 位' }
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请再次输入新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                    return Promise.reject(new Error('两次输入不一致'));
                  }
                })
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Button type="primary" loading={pwdSaving} onClick={() => void submitPassword()}>
              更新密码
            </Button>
          </Form>
          <Divider />
        </Card>
      </div>
    </div>
  );
};

export default MerchantShopCenter;
